/**
 * ProgressIndicator.ts — Achievement progress tracking UI component.
 *
 * Provides:
 * - "X / Y" format progress display for multi-step achievements
 * - Real-time progress updates
 * - Progress bar visualization
 * - Completion detection and unlock triggering
 */

import type { Achievement } from '@game/data/types';
import type { AchievementStatus } from '@core/systems/AchievementSystem';

// --- Types --------------------------------------------------------------

export interface ProgressDisplayConfig {
    /** Show progress bar in addition to numbers */
    showBar: boolean;
    /** Width of progress bar in characters */
    barWidth: number;
    /** Character for filled portion */
    fillChar: string;
    /** Character for empty portion */
    emptyChar: string;
}

const DEFAULT_CONFIG: ProgressDisplayConfig = {
    showBar: true,
    barWidth: 20,
    fillChar: '█',
    emptyChar: '░',
};

// --- Progress Indicator -------------------------------------------------

export class ProgressIndicator {
    private config: ProgressDisplayConfig;

    constructor(config: Partial<ProgressDisplayConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    /**
     * Format progress as "X / Y" string.
     */
    formatProgress(current: number, target: number): string {
        return `${current} / ${target}`;
    }

    /**
     * Format progress with achievement name.
     */
    formatWithName(def: Achievement, status: AchievementStatus): string {
        if (!status.progress) {
            return def.name;
        }
        // Find the first requirement with a progress target
        const progressReq = def.requirements?.find(r => typeof r.value === 'number' && r.value > 1);
        const target = progressReq ? Number(progressReq.value) : 1;
        return `${def.name}: ${this.formatProgress(status.progress, target)}`;
    }

    /**
     * Render a progress bar.
     */
    renderBar(current: number, target: number): string {
        const { barWidth, fillChar, emptyChar } = this.config;
        const ratio = Math.min(current / target, 1);
        const filled = Math.round(ratio * barWidth);
        const empty = barWidth - filled;

        return '[' + fillChar.repeat(filled) + emptyChar.repeat(empty) + ']';
    }

    /**
     * Render full progress display with optional bar.
     */
    render(current: number, target: number): string {
        const text = this.formatProgress(current, target);

        if (this.config.showBar) {
            return `${this.renderBar(current, target)} ${text}`;
        }

        return text;
    }

    /**
     * Render progress for an achievement.
     */
    renderAchievement(def: Achievement, status: AchievementStatus): string {
        if (!status.progress || status.progress === 0) {
            return status.unlocked ? '✓ Complete' : '○ Not started';
        }

        // Find the first requirement with a progress target
        const progressReq = def.requirements?.find(r => typeof r.value === 'number' && r.value > 1);
        const target = progressReq ? Number(progressReq.value) : 1;
        const current = status.progress;

        if (status.unlocked) {
            return `✓ ${def.name} - Complete!`;
        }

        return `${def.name}\n  ${this.render(current, target)}`;
    }

    /**
     * Check if progress has reached the target.
     */
    isComplete(current: number, target: number): boolean {
        return current >= target;
    }

    /**
     * Check if an achievement's progress is complete.
     */
    isAchievementComplete(def: Achievement, status: AchievementStatus): boolean {
        if (!status.progress) {
            return status.unlocked;
        }
        const progressReq = def.requirements?.find(r => typeof r.value === 'number' && r.value > 1);
        const target = progressReq ? Number(progressReq.value) : 1;
        return status.progress >= target;
    }

    /**
     * Calculate percentage complete (0-100).
     */
    getPercentage(current: number, target: number): number {
        if (target <= 0) return 0;
        return Math.min(Math.round((current / target) * 100), 100);
    }
}

// --- Factory ------------------------------------------------------------

let progressIndicatorInstance: ProgressIndicator | null = null;

/**
 * Gets the global progress indicator instance.
 */
export function getProgressIndicator(): ProgressIndicator {
    if (!progressIndicatorInstance) {
        progressIndicatorInstance = new ProgressIndicator();
    }
    return progressIndicatorInstance;
}

/**
 * Resets the progress indicator instance (for testing).
 */
export function resetProgressIndicator(): void {
    progressIndicatorInstance = null;
}
